import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  IconResumoDeskChecklist,
  IconResumoDeskNotas,
  IconResumoDeskTarefas,
  IconResumoDeskTimeline,
  IconVerDash,
} from "./Icons";
import { Checklist } from "@/components/Checklist";
import { Timeline } from "./Timeline/Timeline";
import { Tarefas } from "@/components/Tarefas";
import { Notas } from "@/components/Notas";
import {
  Building2,
  CalendarDays,
  CircleDollarSign,
  Gauge,
  Landmark,
  MapPin,
  Maximize2,
  MessageCircle,
  FileText,
  Minimize2,
  X,
  Tag,
  Video,
  UsersRound,
} from "lucide-react";
import { AddeskChat } from "@/components/AddeskChat";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { ServiceResumoDesk } from "@/services/serviceResumoDesk";
import { toast } from "react-toastify";
import { IconOverviewVerDesk } from "../../Overviews/components/IconsOverview";
// import { IconClose } from "@/assets/images/Icons";
import ImageGenerict from "@/assets/images/lg.png";
import { InformationContato } from "@/components/utils/InformationContato";
import { ResumoAdLink } from "./AdLink";
import { FichaTecnica } from "./components/FichaTecnica";
import { Financeiro } from "./Financeiro";
import { useTranslation } from "react-i18next";
import {
  DeskRateioBadge,
  isDeskRateada,
} from "../components/DeskRateioResumo";
import { timelineItemBelongsToDepartamento } from "@/utils/timelineDepartamento";
import {
  getBriefingDepartamentalBlocks as resolveBriefingDepartamentalBlocks,
} from "@/utils/briefingDepartamental";
import { JORNADA_IDS } from "@/constants/jornadaIds";
import {
  ControleProducaoCard,
  ControleProducaoContent,
  type ControleProducaoView,
} from "./ControleProducao";
import { MesaMidiaAcompanhamento } from "./MesaMidiaAcompanhamento";
import { AtaReuniao } from "./AtaReuniao";

type UnknownRecord = Record<string, unknown>;
type DepartamentoResumo = {
  id: number;
  nome: string;
  type_jornada?: number;
};
const BRIEFING_BLOCK_PREVIEW_LIMIT = 260;

function isRecord(value: unknown): value is UnknownRecord {
  return Boolean(value) && typeof value === "object";
}

function getStringField(value: unknown) {
  return typeof value === "string" ? value : undefined;
}

function getDisplayValue(value: unknown) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function getResumoUserImage(user: unknown) {
  if (!isRecord(user)) return "";

  const directImage =
    getStringField(user.profilePhoto) ??
    getStringField(user.filePath) ??
    getStringField(user.file_path) ??
    getStringField(user.foto) ??
    getStringField(user.image) ??
    getStringField(user.avatar);

  if (directImage) return directImage;
  if (typeof user.url_img === "string") return user.url_img;
  if (isRecord(user.url_img)) return getStringField(user.url_img.path) ?? "";

  return "";
}

function normalizeArray<T = unknown>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
      return [];
    }
  }
  if (isRecord(value) && Array.isArray(value.data)) return value.data as T[];
  return [];
}

function stripHtml(value: string) {
  return String(value ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getBriefingBlockKey(block: UnknownRecord, index: number) {
  return `briefing-resumo-${
    block.departamento_id ?? block.departamento_nome ?? "departamento"
  }-${index}`;
}

function shouldLimitBriefingBlock(value: unknown) {
  return (
    stripHtml(typeof value === "string" ? value : "").length >
    BRIEFING_BLOCK_PREVIEW_LIMIT
  );
}

function normalizeDepartamentoName(value: unknown) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function getBriefingStatusName(block: UnknownRecord) {
  const status = isRecord(block.status)
    ? (block.status.label ?? block.status.name)
    : block.status;

  return (
    getDisplayValue(block.status_nome) ||
    getDisplayValue(status)
  ).replace(/^\d+\s-\s/, "");
}

function getBriefingResponsavelImage(
  block: UnknownRecord,
  responsaveis: unknown[],
) {
  const savedImage = getDisplayValue(block.responsavel_imagem);
  if (savedImage) return savedImage;

  const responsavelId = getDisplayValue(block.responsavel_id);
  if (!responsavelId) return "";

  const responsavel = responsaveis.find((item) => {
    if (!isRecord(item)) return false;
    return (
      getDisplayValue(item.id) === responsavelId ||
      getDisplayValue(item.value) === responsavelId ||
      getDisplayValue(item.user_id) === responsavelId
    );
  });

  return getResumoUserImage(responsavel);
}

type ResumoMentionUser = {
  id: number;
  name: string;
  last_name?: string;
  email?: string;
  url_img?: { path?: string } | string | null;
  profilePhoto?: string;
  filePath?: string;
};

function getResumoMentionUserName(user: unknown) {
  if (!isRecord(user)) return "";

  const name = getStringField(user.name) ?? "";
  const lastName = getStringField(user.last_name) ?? "";

  return (
    `${name} ${lastName}`.trim() ||
    getStringField(user.label) ||
    getStringField(user.nome) ||
    ""
  );
}

function toResumoMentionUser(user: unknown): ResumoMentionUser | null {
  if (!isRecord(user)) return null;

  const userId = Number(user.id ?? user.value ?? user.user_id);
  const userName = getResumoMentionUserName(user);

  if (!Number.isFinite(userId) || !userName) return null;

  const [name, ...lastNameParts] = userName.split(" ");

  return {
    id: userId,
    name: getStringField(user.name) ?? name,
    last_name: getStringField(user.last_name) ?? lastNameParts.join(" "),
    email: getStringField(user.email),
    url_img:
      (user.url_img as ResumoMentionUser["url_img"]) ??
      (user.image as ResumoMentionUser["url_img"]) ??
      (user.avatar as ResumoMentionUser["url_img"]) ??
      null,
    profilePhoto: getStringField(user.profilePhoto),
    filePath:
      getStringField(user.filePath) ??
      getStringField(user.file_path) ??
      getStringField(user.foto),
  };
}

function collectResumoUsersFrom(value: unknown) {
  return normalizeArray(value)
    .map((item) => toResumoMentionUser(item))
    .filter((user): user is ResumoMentionUser => Boolean(user));
}

function getResumoEntityName(value: unknown) {
  if (!isRecord(value)) return "";

  return (
    getDisplayValue(value.name_fantasy) ||
    getDisplayValue(value.nome) ||
    getDisplayValue(value.name) ||
    getDisplayValue(value.label) ||
    getDisplayValue(value.title)
  );
}

function getResumoVehicleNames(data: unknown) {
  if (!isRecord(data)) return [];

  const vehicleNames = [
    getResumoEntityName(data.representado),
    getResumoEntityName(data.veiculo),
    getResumoEntityName(data.vehicle),
    getDisplayValue(data.veiculo_name),
    getDisplayValue(data.vehicle_name),
  ];

  normalizeArray(data.proposta).forEach((item) => {
    if (!isRecord(item)) return;

    vehicleNames.push(
      getResumoEntityName(item.veiculo),
      getResumoEntityName(item.vehicle),
      getDisplayValue(item.veiculo_name),
      getDisplayValue(item.vehicle_name),
    );
  });

  return Array.from(
    new Set(vehicleNames.filter((name) => name && name !== "-")),
  );
}

function getResumoUserFullName(user: unknown) {
  if (!isRecord(user)) return "-";

  const fullName = `${getDisplayValue(user.name)} ${getDisplayValue(
    user.last_name,
  )}`.trim();

  return (
    fullName ||
    getDisplayValue(user.nome) ||
    getDisplayValue(user.label) ||
    "-"
  );
}

function getResumoUserFirstName(user: unknown) {
  const name = getResumoUserFullName(user);
  if (name === "-") return "-";

  return name.split(" ").filter(Boolean)[0] ?? name;
}

function getResumoDateLabel(data: unknown, type: unknown) {
  if (!isRecord(data)) return "-";

  if (type === "desk") {
    const start = data.date_init
      ? moment.utc(String(data.date_init)).format("DD/MM/YYYY")
      : "";
    const end = data.date_end
      ? moment.utc(String(data.date_end)).format("DD/MM/YYYY")
      : "";

    if (start && end) return `${start} a ${end}`;
    return start || end || "-";
  }

  if (type === "reunion" && data.date) {
    return moment.utc(String(data.date)).format("DD/MM/YYYY - HH:mm");
  }

  if (type === "prospecting" && data.date) {
    return moment.utc(String(data.date)).format("DD/MM/YYYY");
  }

  if (type === "campanha") {
    return `${data.mes ? String(data.mes).padStart(2, "0") : "--"}/${
      data.ano ?? "----"
    }`;
  }
  if (type === "cronograma" && data.data) {
    const horario = getDisplayValue(data.horario);
    return `${moment.utc(String(data.data)).format("DD/MM/YYYY")}${horario ? ` - ${horario}` : ""}`;
  }

  return "-";
}

export function ResumoDesk({
  togleOpen,
  data,
  client,
  status,
  agency,
  type,
  totalMidia,
  initialTab = "timeline",
  onChatOpened,
}: any) {
  const { t } = useTranslation();
  const [optionResume, setOptionResume] = useState(initialTab);
  const [controleProducaoView, setControleProducaoView] =
    useState<ControleProducaoView>("dashboard");
  const [controleProducaoAction, setControleProducaoAction] = useState<{
    view: ControleProducaoView;
    nonce: number;
  } | null>(null);
  const [controleProducaoOpen, setControleProducaoOpen] = useState(false);
  const previousInitialTabRef = useRef(initialTab);
  const [verMaisBrifing, setVerMaisBrifing] = useState<boolean>(true);
  const [briefingBlocksExpanded, setBriefingBlocksExpanded] = useState<
    Record<string, boolean>
  >({});
  const [departamentos, setDepartamentos] = useState<DepartamentoResumo[]>([]);
  const [departamentoActive, setDepartamentoActive] = useState(0);
  const textoRef = useRef<HTMLParagraphElement>(null);
  const [mostrarBotao, setMostrarBotao] = useState(false);
  const [componenteAberto, setComponenteAberto] = useState<
    "fichaTecnica" | "adLink" | null
  >(null);
  const [resumoExpanded, setResumoExpanded] = useState(true);
  const locationURL = useLocation();
  const hasControleProducao =
    type === "desk" && Number(data?.jornada_id) === JORNADA_IDS.NAO_MIDIA;
  const isRelacionamentoMidiaResumoDesk =
    type === "desk" &&
    (Number(data?.jornada_id) === JORNADA_IDS.RELACIONAMENTO_MIDIA ||
      [1, 4].includes(Number(data?.type_desk_id)));
  const selectedDepartamento = useMemo(
    () =>
      departamentos.find(
        (departamento) => departamento.id === departamentoActive,
      ),
    [departamentos, departamentoActive],
  );
  const hasAcompanhamentoMesaMidia =
    isRelacionamentoMidiaResumoDesk &&
    normalizeDepartamentoName(selectedDepartamento?.nome) === "mesa de midia";
  const hasWideResumoModal =
    type === "reunion" || hasControleProducao || isRelacionamentoMidiaResumoDesk;

  useEffect(() => {
    if (!hasAcompanhamentoMesaMidia) {
      if (optionResume === "trading") {
        setOptionResume("timeline");
      }
    }
  }, [hasAcompanhamentoMesaMidia, optionResume]);

  useEffect(() => {
    const queryParams = new URLSearchParams(locationURL.search);
    if (queryParams.has("email")) {
      setOptionResume("tarefas");
    }
  }, [locationURL.search]);

  useEffect(() => {
    if (previousInitialTabRef.current !== initialTab) {
      previousInitialTabRef.current = initialTab;
      setOptionResume(initialTab);
    }
  }, [initialTab]);

  useEffect(() => {
    if (optionResume === "chat" && onChatOpened) {
      onChatOpened();
    }
  }, [optionResume, onChatOpened]);

  useEffect(() => {
    getDepartamentos();
    const el: any = textoRef.current;
    if (el) {
      const precisaClamp = el.scrollHeight > el.clientHeight;
      setMostrarBotao(precisaClamp);
    }
  }, []);

  async function getDepartamentos() {
    try {
      const res = await ServiceResumoDesk.getDepartamentos(1, 999);

      if (res.status === 200) {
        setDepartamentos(res.data.data);
      } else {
        toast.error(t("common:fetch_app_settings_error"));
      }
    } catch (error) {
      toast.error(t("common:fetch_app_settings_error"));
    }
  }
  const [timeline, setTimeline] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const responsaveisResumo = normalizeArray(data?.responsaveis);
  const briefingDepartamentalBlocks = resolveBriefingDepartamentalBlocks(data);
  const hasBriefingResumo =
    briefingDepartamentalBlocks.length > 0 || Boolean(data?.objetivo);
  const resumoParticipantes = useMemo(
    () => collectResumoUsersFrom(data?.integrantes),
    [data?.integrantes],
  );
  const mentionUsers = useMemo(() => {
    const users = new Map<number, ResumoMentionUser>();
    const addUser = (user: unknown) => {
      const mentionUser = toResumoMentionUser(user);
      if (mentionUser) users.set(mentionUser.id, mentionUser);
    };
    const addUsers = (value: unknown) => {
      collectResumoUsersFrom(value).forEach((user) => {
        users.set(user.id, user);
      });
    };

    [
      data?.criador,
      data?.user,
      data?.responsavel,
      data?.protagonista,
      data?.organizer,
      data?.owner,
      data?.prospecting?.responsavel,
      data?.prospeccao?.responsavel,
    ].forEach(addUser);

    [
      data?.responsaveis,
      data?.vendedores,
      data?.shared_users,
      data?.users_time_agency,
      data?.users_time_goon,
      data?.users,
      data?.participants,
      data?.participantes,
      data?.integrantes,
      data?.convidados,
      data?.prospecting?.responsaveis,
      data?.prospecting?.vendedores,
      data?.prospecting?.shared_users,
      data?.prospeccao?.responsaveis,
      data?.prospeccao?.vendedores,
      data?.prospeccao?.shared_users,
    ].forEach(addUsers);

    return Array.from(users.values());
  }, [data]);

  useEffect(() => {
    getTimeline();
  }, [departamentoActive, data?.id, type]);

  async function getTimeline() {
    if (!data?.id || !type) {
      return;
    }

    try {
      const { token } = JSON.parse(localStorage.getItem("token") || "{}");
      if (token) {
        setLoading(true);
        const responseTask = await ServiceResumoDesk.getTimeline(
          data.id,
          type,
          departamentoActive !== 0
            ? { id_departamento: departamentoActive }
            : undefined,
        );
        if (responseTask.status === 200) {
          setTimeline(responseTask.data);
        }
      }
    } catch (error) {
      toast.error(t("tasks.messages.fetchError"));
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const filteredTimeline = timeline?.data?.filter(
    (item: any) =>
      item.alteracao === "change status" &&
      timelineItemBelongsToDepartamento(item, departamentoActive),
  );

  const getLastObject = (data: any) => {
    const sortedData = data?.sort(
      (a: any, b: any) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    if (sortedData && sortedData.length > 0) {
      return sortedData[0];
    }
  };

  const produtos = [
    { value: 1, label: t("resumoDesk.products.social_media") },
    { value: 2, label: t("resumoDesk.products.midia_display") },
    { value: 3, label: t("resumoDesk.products.web") },
    { value: 4, label: t("resumoDesk.products.crm") },
    { value: 5, label: t("resumoDesk.products.offline") },
    { value: 6, label: t("resumoDesk.products.branding") },
    { value: 7, label: t("resumoDesk.products.copy") },
    { value: 8, label: t("resumoDesk.products.seo") },
    { value: 9, label: t("resumoDesk.products.planning") },
  ];

  const handleToggleFichaTecnica = () => {
    setComponenteAberto((prev) =>
      prev === "fichaTecnica" ? null : "fichaTecnica",
    );
  };

  const handleToggleAdLink = () => {
    setComponenteAberto((prev) => (prev === "adLink" ? null : "adLink"));
  };

  const openControleProducaoDashboard = () => {
    setControleProducaoOpen(true);
    setControleProducaoView("dashboard");
    setOptionResume("controleProducao");
  };

  const openControleProducaoMain = () => {
    setControleProducaoOpen(true);
    setOptionResume("controleProducao");
  };

  const toggleControleProducao = () => {
    if (!controleProducaoOpen) {
      openControleProducaoMain();
      return;
    }

    setControleProducaoOpen(false);
  };

  const lastStatusTimeline = getLastObject(filteredTimeline);
  const resumoStatusName =
    (isRecord(status) ? getResumoEntityName(status) : getDisplayValue(status)) ||
    getResumoEntityName(data?.status);
  const resumoStatusDisplay = resumoStatusName.replace(/^\d+\s-\s/, "");
  const resumoDateLabel = getResumoDateLabel(data, type);
  const resumoVehicleNames = getResumoVehicleNames(data);
  const resumoRelationItems = [
    agency && agency !== "-" ? { label: "Agência", value: agency } : null,
    client && client !== "-"
      ? { label: "Cliente", value: client }
      : null,
    ...resumoVehicleNames.map((value) => ({ label: "Veículo", value })),
  ].filter((item): item is { label: string; value: string } => Boolean(item));
  const resumoOpenedLabel = lastStatusTimeline
    ? `${moment().diff(lastStatusTimeline?.createdAt, "days")} ${t(
        "resumoDesk.days_in_status",
      )}`
    : t("resumoDesk.opened_days", {
        days: moment().diff(data.createdAt ?? data.created_at, "days"),
      });

  const renderResumoInfoRow = (
    Icon: typeof CalendarDays,
    label: string,
    content: ReactNode,
  ) => {
    if (!content) return null;

    return (
      <div className="grid grid-cols-[20px_minmax(0,1fr)] gap-3">
        <div className="pt-0.5 text-cinza-700">
          <Icon size={17} strokeWidth={1.9} />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-medium leading-tight text-cinza-400">
            {label}
          </p>
          <div className="mt-1 text-sm font-medium leading-snug text-cinza-800">
            {content}
          </div>
        </div>
      </div>
    );
  };

  const renderResumoContact = (label: string, user: unknown) => {
    const name = getResumoUserFullName(user);
    if (name === "-") return null;

    const photo = getResumoUserImage(user) || ImageGenerict;
    const userRecord = isRecord(user) ? user : {};
    const firstName = getResumoUserFirstName(user);

    return (
      <div className="min-w-0">
        <p className="mb-1 text-[11px] font-medium leading-tight text-cinza-400">
          {label}
        </p>
        <div
          title={name}
          className="flex w-fit max-w-full items-center gap-1.5 rounded-full bg-black/5 px-2 py-1"
        >
          <img
            src={photo}
            alt={name}
            className="h-5 w-5 shrink-0 rounded-full object-cover"
          />
          <div className="min-w-0 max-w-[92px] text-[11px] font-semibold text-cinza-800">
            <InformationContato
              data={user}
              id={Number(userRecord.id) || undefined}
              name={firstName}
              foto={photo}
            />
          </div>
        </div>
      </div>
    );
  };

  const resumoOrganizer = data?.user ?? data?.criador;
  const resumoResponsible = data?.responsavel ?? responsaveisResumo[0];
  const resumoProtagonist = data?.protagonista;
  const hasResumoPeople = [resumoOrganizer, resumoResponsible, resumoProtagonist]
    .map((user) => getResumoUserFullName(user))
    .some((name) => name !== "-");

  return (
    <div className={`relative ${type === "cronograma" ? "z-[70]" : ""}`}>
      {type !== "cronograma" && (
        <div
          className="bg-black/30 fixed top-0 bottom-0 right-0 left-0 z-10"
          onClick={() => togleOpen(false)}
        />
      )}
      <div className={`fixed top-4 sm:top-10 left-0 mx-2 max-w-[calc(100vw-1rem)] space-y-3 sm:left-auto sm:right-1 sm:mx-0 sm:flex sm:max-w-[calc(100vw-0.5rem)] sm:items-start sm:gap-x-4 sm:space-y-0 sm:px-4 md:right-12 md:max-w-[calc(100vw-6rem)] md:px-0 ${type === "cronograma" ? "z-[72]" : "z-30"}`}>
        {/*    <button
          className="p-2 bg-azul-400/20 rounded-full hover:bg-azul-400 text-azul-400 hover:text-white duration-300"
          onClick={() => togleOpen(false)}
          title={t("common:close")}
        >
          <IconClose />
        </button> */}
        <div className="flex flex-col gap-4 sm:w-72 sm:shrink-0">
          <FichaTecnica
            data={data}
            agency={agency}
            client={client}
            status={status}
            produtos={produtos}
            type={type}
            totalMidia={totalMidia}
            isOpen={componenteAberto === "fichaTecnica"}
            onToggle={handleToggleFichaTecnica}
          />
          {(type === "desk" || type === "reunion") && (
            <ResumoAdLink
              sharedUsers={data?.shared_users}
              isOpen={componenteAberto === "adLink"}
              onToggle={handleToggleAdLink}
              deskId={data?.id}
              itemType={type}
              links={data.links}
              links_adlink={data.links_adlink}
            />
          )}
          {hasControleProducao && (
            <ControleProducaoCard
              desk={data}
              activeView={controleProducaoView}
              onChangeView={setControleProducaoView}
              isOpen={controleProducaoOpen}
              onToggle={toggleControleProducao}
              onOpenMain={openControleProducaoMain}
              onPrimaryAction={(view) => {
                openControleProducaoMain();
                setControleProducaoView(view);
                setControleProducaoAction({ view, nonce: Date.now() });
              }}
            />
          )}
        </div>
        <div
          role={type === "cronograma" ? "dialog" : undefined}
          aria-modal={type === "cronograma" ? "true" : undefined}
          aria-label={type === "cronograma" ? "Ficha técnica do post" : undefined}
          className={`relative w-[95vw] max-w-[calc(100vw-1rem)] rounded-3xl bg-white p-6 shadow-xl transition-[width,max-width] duration-300 ease-out sm:min-h-[90%] sm:min-w-0 sm:max-w-[calc(100vw-22rem)] md:max-w-[calc(100vw-26rem)] ${
            hasWideResumoModal && resumoExpanded
              ? "sm:w-[calc(100vw-22rem)] md:w-[calc(100vw-26rem)] xl:w-[1100px]"
              : "sm:w-[576px] md:w-[672px]"
          } max-h-[50vh] overflow-x-hidden overflow-y-auto sm:max-h-[90vh]`}
        >
          {type === "cronograma" && (
            <button
              type="button"
              onClick={() => togleOpen(false)}
              className="absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-black/5 text-black/50 transition-colors hover:bg-black/10"
              aria-label="Fechar ficha técnica"
            >
              <X size={16} />
            </button>
          )}
          <div
            className={`flex justify-between items-center gap-x-4 ${
              data?.jornada_id === 2 ? "space-y-0" : "flex-wrap space-y-4"
            }`}
          >
            <div
              className={`sm:flex justify-around text-center rounded-2xl bg-[#EBEBEB] text-sm ${
                data?.jornada_id === 2 ? "w-8/12" : "w-full"
              }`}
            >
              {type !== "cronograma" && departamentos
                ?.filter((e) => {
                  if (data.jornada_id === 7) {
                    return e.type_jornada === 0 || e.type_jornada === 3;
                  } else if (
                    data.jornada_id === 1 ||
                    !data.jornada_id ||
                    data.jornada_id === 4
                  ) {
                    return e.type_jornada === 0 || e.type_jornada === 1;
                  } else if (data.jornada_id === 2) {
                    return e.type_jornada === 0 || e.type_jornada === 2;
                  }
                  return false;
                })
                ?.sort((a: any, b: any) => a.id - b.id)
                .map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => setDepartamentoActive(e.id)}
                    className={`px-1 md:px-2 py-1 rounded-2xl w-full ${
                      e.id === departamentoActive
                        ? "bg-azul-400 text-white"
                        : "text-zinc-400"
                    }`}
                  >
                    {e.nome}
                  </button>
                ))}
            </div>
            {type === "desk" && (
              <div className="flex flex-wrap gap-3 justify-end">
                {hasWideResumoModal && (
                  <button
                    type="button"
                    onClick={() => setResumoExpanded((current) => !current)}
                    className="flex items-center justify-center gap-2 rounded-full border border-cinza-200 px-3 py-1 text-sm text-cinza-700 transition hover:border-azul-400 hover:text-azul-400"
                    title={
                      resumoExpanded ? "Reduzir janela" : "Expandir janela"
                    }
                  >
                    {resumoExpanded ? (
                      <Minimize2 size={15} />
                    ) : (
                      <Maximize2 size={15} />
                    )}
                    {resumoExpanded ? "Reduzir" : "Expandir"}
                  </button>
                )}
                {data.hash && data.numero_pi && (
                  <a
                    href={`${location.origin}/dashboard/${data.hash}/["${data.numero_pi}"]`}
                    target="_blank"
                    className="py-1 px-3 bg-azul-400 text-white text-sm flex justify-center items-center gap-2 rounded-full"
                  >
                    {t("resumoDesk.buttons.view_dash")} <IconVerDash />
                  </a>
                )}
                <a
                  href={`${location.origin}/desks/${data.id}`}
                  target="_blank"
                  className="py-1 px-3 bg-azul-400 text-white text-sm flex justify-center items-center gap-2 rounded-full"
                >
                  {data.jornada_id == 1 && (
                    <>
                      {t("resumoDesk.buttons.view_desk")}{" "}
                      <IconOverviewVerDesk />
                    </>
                  )}
                  {data.jornada_id == 2 && (
                    <>
                      {t("resumoDesk.buttons.view_job")} <IconOverviewVerDesk />
                    </>
                  )}
                </a>
              </div>
            )}
            {type === "reunion" && data?.id_prospecting && (
              <div className="flex flex-wrap gap-3 justify-end">
                <a
                  href={`${location.origin}/prospeccoes/${data.id_prospecting}`}
                  target="_blank"
                  className="py-1 px-3 bg-azul-400 text-white text-sm flex justify-center items-center gap-2 rounded-full"
                >
                  {t("resumoDesk.buttons.view_prospecting")}{" "}
                  <IconOverviewVerDesk />
                </a>
              </div>
            )}
          </div>

          <section className="pt-6">
            <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(300px,0.9fr)] xl:items-start">
              <div className="min-w-0 space-y-4">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="min-w-0 break-words text-2xl font-semibold uppercase leading-tight tracking-normal text-[#1E1E1E] md:text-[26px]">
                      {data?.name ?? data?.title ?? data?.titulo ?? data?.conceito}
                    </h2>
                    {type === "desk" && isDeskRateada(data) && (
                      <DeskRateioBadge compact />
                    )}
                    {!!data?.priority?.id && (
                      <div
                        className={`rounded-full px-2 py-1 font-medium ${
                          data?.priority?.id === 1 && "bg-red-500 text-white"
                        } ${
                          data?.priority?.id === 2 &&
                          "bg-goon-amareloClaro text-cinza-800"
                        } ${
                          data?.priority?.id === 3 && "bg-verde-300 text-white"
                        } `}
                      >
                        <p className="text-xs text-center">
                          {data.jornada_id == 2
                            ? t("resumoDesk.priority")
                            : t("resumoDesk.criticality")}{" "}
                          -{data?.priority?.name}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-medium text-cinza-300">
                    {!!resumoStatusDisplay && (
                      <span className="flex items-center gap-1.5 text-cinza-500">
                        <span
                          className={`h-3 w-3 rounded-full ${
                            [21, 23, 26, 28].includes(data?.status_id)
                              ? "bg-azul-400"
                              : [22, 24, 27, 29].includes(data?.status_id)
                                ? "bg-verde-300"
                                : [70].includes(data?.status_id)
                                  ? "bg-red-500"
                                  : "bg-amber-400"
                          }`}
                        />
                        {resumoStatusDisplay}
                      </span>
                    )}
                    <span>{resumoOpenedLabel}</span>
                  </div>
                </div>

                {type === "reunion" && data?.meet_link && (
                  <a
                    href={data.meet_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex w-fit items-center justify-center gap-2 rounded-full bg-azul-400 px-4 py-2 text-sm text-white shadow-md transition-all duration-200 hover:bg-azul-400/90 hover:shadow-lg"
                  >
                    <Video size={20} strokeWidth={2} />
                    Acessar reunião
                  </a>
                )}
              </div>

              <div className="min-w-0 rounded-[18px] border border-cinza-200 bg-white p-4 md:p-5">
                <div className="space-y-4">
                  {renderResumoInfoRow(
                    CalendarDays,
                    type === "reunion" ? "Data e horário" : "Período",
                    resumoDateLabel,
                  )}
                  {type === "reunion" &&
                    renderResumoInfoRow(
                      MapPin,
                      "Local",
                      getDisplayValue(data?.local) ||
                        getDisplayValue(data?.location) ||
                        "-",
                    )}
                  {!!resumoStatusDisplay &&
                    renderResumoInfoRow(Tag, "Status", resumoStatusDisplay)}
                  {hasResumoPeople &&
                    renderResumoInfoRow(
                      UsersRound,
                      "Pessoas",
                      <div className="grid min-w-0 gap-x-4 gap-y-3 sm:grid-cols-[repeat(auto-fit,minmax(132px,1fr))]">
                        {renderResumoContact(
                          t("resumoDesk.organizer"),
                          resumoOrganizer,
                        )}
                        {renderResumoContact(
                          t("resumoDesk.responsible"),
                          resumoResponsible,
                        )}
                        {renderResumoContact(
                          t("resumoDesk.protagonist"),
                          resumoProtagonist,
                        )}
                      </div>,
                    )}
                  {renderResumoInfoRow(
                    Building2,
                    "Agência / Cliente / Veículo",
                    resumoRelationItems.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {resumoRelationItems.map((item, index) => (
                          <span
                            key={`${item.label}-${item.value}-${index}`}
                            title={item.label}
                            className="max-w-full truncate rounded-full bg-[#DDE8FF] px-3 py-1 text-[11px] font-semibold text-[#3151CF]"
                          >
                            {item.value}
                          </span>
                        ))}
                      </div>
                    ) : (
                      "-"
                    ),
                  )}
                  {type === "reunion" &&
                    resumoParticipantes.length > 0 &&
                    renderResumoInfoRow(
                      UsersRound,
                      `Participantes (${resumoParticipantes.length})`,
                      <div className="flex flex-wrap items-center gap-2">
                        {resumoParticipantes.slice(0, 7).map((user) => {
                          const photo =
                            getResumoUserImage(user) || ImageGenerict;
                          const name = getResumoUserFullName(user);
                          const firstName = getResumoUserFirstName(user);

                          return (
                            <div
                              key={user.id}
                              title={name}
                              className="flex max-w-full items-center gap-1.5 rounded-full bg-black/5 px-2 py-1"
                            >
                              <img
                                src={photo}
                                alt={name}
                                className="h-5 w-5 shrink-0 rounded-full object-cover"
                              />
                              <div className="min-w-0 max-w-[90px] text-[11px] font-semibold text-cinza-800">
                                <InformationContato
                                  data={user}
                                  id={user.id}
                                  name={firstName}
                                  foto={photo}
                                />
                              </div>
                            </div>
                          );
                        })}
                        {resumoParticipantes.length > 7 && (
                          <span className="rounded-full bg-[#DDE8FF] px-2 py-1 text-[10px] font-semibold text-[#3151CF]">
                            +{resumoParticipantes.length - 7}
                          </span>
                        )}
                      </div>,
                    )}
                </div>
              </div>
            </div>
          </section>

          <div className="pt-6">
            {hasBriefingResumo && (
              <div className="rounded-[18px] border border-cinza-200 bg-white p-3 md:p-5 space-y-4 text-xs">
                <h3 className="text-black font-semibold text-base">
                  {briefingDepartamentalBlocks.length > 0
                    ? t("resumoDesk.departmentBriefing")
                    : t("resumoDesk.briefing")}
                </h3>
                <div
                  ref={textoRef}
                  className={`linkColor text-black font-normal transition-all ${
                    briefingDepartamentalBlocks.length === 0
                      ? !verMaisBrifing
                        ? "line-clamp-none"
                        : "line-clamp-3"
                      : ""
                  }`}
                >
                  {briefingDepartamentalBlocks.length > 0 ? (
                    <div className="space-y-3">
                      {briefingDepartamentalBlocks.map((block, index) => {
                        const blockKey = getBriefingBlockKey(block, index);
                        const departamentoNome = getDisplayValue(
                          block.departamento_nome,
                        );
                        const responsavelNome = getDisplayValue(
                          block.responsavel_nome,
                        );
                        const statusNome = getBriefingStatusName(block);
                        const responsavelImage =
                          getBriefingResponsavelImage(
                            block,
                            responsaveisResumo,
                          ) || ImageGenerict;
                        const hasBriefingMeta = Boolean(
                          departamentoNome || responsavelNome || statusNome,
                        );
                        const shouldLimit = shouldLimitBriefingBlock(
                          block.briefing,
                        );
                        const isExpanded = Boolean(
                          briefingBlocksExpanded[blockKey],
                        );

                        return (
                          <div
                            key={blockKey}
                            className="rounded-xl border border-cinza-200 bg-white p-3 md:p-4 shadow-sm"
                          >
                            <div
                              className={`prose prose-sm max-w-none text-sm leading-relaxed text-black ${
                                shouldLimit && !isExpanded
                                  ? "line-clamp-3"
                                  : ""
                              }`}
                              dangerouslySetInnerHTML={{
                                __html: String(block.briefing ?? ""),
                              }}
                            />
                            {hasBriefingMeta && (
                              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-cinza-100 pt-2 text-[11px] leading-none text-cinza-500">
                                {departamentoNome && (
                                  <span className="font-medium text-azul-400">
                                    {departamentoNome}
                                  </span>
                                )}
                                {responsavelNome && (
                                  <span className="flex min-w-0 items-center gap-1.5">
                                    <img
                                      src={responsavelImage}
                                      alt={responsavelNome}
                                      className="h-5 w-5 shrink-0 rounded-full object-cover"
                                    />
                                    <span className="text-cinza-400">
                                      {t("jobs.view.fields.responsible")}:
                                    </span>
                                    <span className="max-w-[190px] truncate text-cinza-500">
                                      {responsavelNome}
                                    </span>
                                  </span>
                                )}
                                {statusNome && (
                                  <span className="rounded-full bg-cinza-100 px-2 py-1 text-[10px] font-medium text-cinza-600">
                                    {statusNome}
                                  </span>
                                )}
                              </div>
                            )}
                            {shouldLimit && (
                              <button
                                type="button"
                                onClick={() =>
                                  setBriefingBlocksExpanded((previous) => ({
                                    ...previous,
                                    [blockKey]: !previous[blockKey],
                                  }))
                                }
                                className="mt-2 text-[11px] font-medium text-azul-400 hover:text-azul-300"
                              >
                                {isExpanded
                                  ? t("common:see_less")
                                  : t("common:see_more")}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: data?.objetivo,
                      }}
                    />
                  )}
                </div>
                {mostrarBotao && briefingDepartamentalBlocks.length === 0 && (
                  <button
                    onClick={() => setVerMaisBrifing(!verMaisBrifing)}
                    className="bg-[#AFE2F6] px-3 py-1 text-[10px] rounded-full text-[#153DCE] flex items-center gap-x-1"
                  >
                    {t("common:see_all")}
                    <svg
                      width="7"
                      height="4"
                      viewBox="0 0 7 4"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.68921 1.34546L4.05086 2.98381C3.66033 3.37434 3.02717 3.37434 2.63664 2.98381L0.998291 1.34546"
                        stroke="#153DCE"
                        stroke-linecap="round"
                      />
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>
          <div>
            <div>
              <div className="py-6 flex flex-wrap gap-x-4 justify-center text-sm">
                <button
                  type="button"
                  className={`flex gap-2 items-center justify-center text-black`}
                  onClick={() => {
                    setOptionResume("timeline");
                    void getTimeline();
                  }}
                >
                  <IconResumoDeskTimeline size={24} />
                  <p
                    className={`transition-all ${
                      optionResume === "timeline" && "border-b border-black"
                    }`}
                  >
                    {t("resumoDesk.tabs.timeline")}
                  </p>
                </button>
                <button
                  type="button"
                  className={`flex gap-2 items-center justify-center text-black`}
                  onClick={() => setOptionResume("tarefas")}
                >
                  <IconResumoDeskTarefas />
                  <p
                    className={`transition-all ${
                      optionResume === "tarefas" && "border-b border-black"
                    }`}
                  >
                    {t("menu.tasks")}
                  </p>
                </button>
                <button
                  type="button"
                  className={`flex gap-2 items-center justify-center text-black`}
                  onClick={() => setOptionResume("notas")}
                >
                  <IconResumoDeskNotas />
                  <p
                    className={`transition-all ${
                      optionResume === "notas" && "border-b border-black"
                    }`}
                  >
                    {t("menu.notes")}
                  </p>
                </button>
                {type !== "campanha" && type !== "cronograma" && (
                  <button
                    type="button"
                    className={`flex gap-2 items-center justify-center text-black`}
                    onClick={() => setOptionResume("checklist")}
                  >
                    <IconResumoDeskChecklist size={16} />
                    <p
                      className={`transition-all ${
                        optionResume === "checklist" && "border-b border-black"
                      }`}
                    >
                      {t("resumoDesk.tabs.checklist")}
                    </p>
                  </button>
                )}
                {type === "reunion" && (
                  <button
                    type="button"
                    className="flex items-center justify-center gap-2 text-black"
                    onClick={() => setOptionResume("meetingMinutes")}
                  >
                    <FileText size={16} />
                    <p className={`transition-all ${optionResume === "meetingMinutes" && "border-b border-black"}`}>
                      Ata de reunião
                    </p>
                  </button>
                )}
                {["reunion", "desk"].includes(type) && (
                  <button
                    type="button"
                    className={`flex gap-2 items-center justify-center text-black`}
                    onClick={() => setOptionResume("chat")}
                  >
                    <MessageCircle size={16} />
                    <p
                      className={`transition-all ${
                        optionResume === "chat" && "border-b border-black"
                      }`}
                    >
                      {t("chatModule.title")}
                    </p>
                  </button>
                )}
                {hasAcompanhamentoMesaMidia && (
                  <button
                    type="button"
                    className="flex items-center justify-center gap-2 text-black"
                    onClick={() => {
                      setOptionResume("trading");
                    }}
                  >
                    <Gauge size={16} />
                    <p
                      className={`transition-all ${
                        optionResume === "trading" && "border-b border-black"
                      }`}
                    >
                      Trading
                    </p>
                  </button>
                )}
                {hasControleProducao && (
                  <button
                    type="button"
                    className={`flex gap-2 items-center justify-center text-black`}
                    onClick={openControleProducaoDashboard}
                  >
                    <CircleDollarSign size={16} />
                    <p
                      className={`transition-all ${
                        optionResume === "controleProducao" &&
                        controleProducaoView === "dashboard" &&
                        "border-b border-black"
                      }`}
                    >
                      Produção
                    </p>
                  </button>
                )}
                {type !== "cronograma" && (departamentoActive === 12 || departamentoActive === 29) && (
                  <button
                    type="button"
                    className={`flex gap-2 items-center justify-center`}
                    onClick={() => setOptionResume("financeiro")}
                  >
                    <Landmark size={16} />
                    <p
                      className={`transition-all ${
                        optionResume === "financeiro" && "border-b border-black"
                      }`}
                    >
                      Financeiro
                    </p>
                  </button>
                )}
              </div>
            </div>
            <div>
              {hasAcompanhamentoMesaMidia && optionResume === "trading" && (
                <div className="mb-6">
                  <MesaMidiaAcompanhamento
                    desk={data}
                    onRefreshTimeline={getTimeline}
                  />
                </div>
              )}
              {optionResume === "timeline" && (
                <Timeline
                  timeline={timeline}
                  dadosDesk={data}
                  loading={loading}
                  departamentos={departamentos}
                  departamentoActive={departamentoActive}
                />
              )}
              {optionResume === "tarefas" && (
                <Tarefas
                  data={data}
                  type={type}
                  departamentos={departamentos}
                  departamentoActive={departamentoActive}
                />
              )}
              {optionResume === "notas" && (
                <Notas
                  data={data}
                  type={type}
                  departamentos={departamentos}
                  departamentoActive={departamentoActive}
                />
              )}
              {optionResume === "checklist" && type !== "campanha" && type !== "cronograma" && (
                <Checklist
                  data={data}
                  type={type}
                  departamentos={departamentos}
                  departamentoActive={departamentoActive}
                />
              )}
              {optionResume === "meetingMinutes" && type === "reunion" && (
                <AtaReuniao reunion={data} />
              )}
              {optionResume === "chat" &&
                ["reunion", "desk"].includes(type) && (
                  <div className="h-[500px]">
                    {type === "reunion" ? (
                      <AddeskChat
                        reunionId={data?.id}
                        isReunion={true}
                        mentionUsers={mentionUsers}
                      />
                    ) : (
                      <AddeskChat addeskId={data?.id} mentionUsers={mentionUsers} />
                    )}
                  </div>
                )}
              {optionResume === "financeiro" && (
                <Financeiro
                  data={data}
                  departamentoActive={departamentoActive}
                />
              )}
              {optionResume === "controleProducao" && hasControleProducao && (
                <ControleProducaoContent
                  desk={data}
                  activeView={controleProducaoView}
                  onChangeView={setControleProducaoView}
                  onRefreshTimeline={getTimeline}
                  actionRequest={controleProducaoAction}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
